import { Suspense, lazy } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";
import BlogContent from "@/components/BlogContent";
import NotFound from "@/pages/NotFound";
import POSTS from "@/data/blog/generated/index.json";
import { COMPANY } from "@/data/company";
import { formatDateFr } from "@/lib/format";
import { hy } from "@/lib/hyphenate";
import { trackEvent } from "@/lib/analytics";

const SITE = COMPANY.url;

function BlogBody({ post }) {
  return (
    <>
      <div className="max-w-3xl">
        <BlogContent blocks={post.blocks} />
      </div>
      <section aria-labelledby="sources-title" className="mt-12 max-w-3xl border-t border-ink/20 pt-6 text-sm leading-relaxed text-ink/80">
        <h2 id="sources-title" className="font-display text-xl font-bold mb-3">Sources</h2>
        <ul className="space-y-2">
          {post.sources.map((source) => (
            <li key={source.url}>
              <a href={source.url} target="_blank" rel="noopener noreferrer" className="underline underline-offset-4 hover:text-accent">{source.label}</a>
            </li>
          ))}
        </ul>
        <p className="mt-4 text-xs text-ink/65">
          Article de veille préparé avec l’aide d’une intelligence artificielle à partir des sources
          ci-dessus, qui font foi. Il donne une information générale et ne remplace ni la lecture de
          votre contrat ni l’étude de votre situation par un professionnel.
        </p>
      </section>
    </>
  );
}

// Texte de chaque article dans un fichier séparé, chargé seulement sur sa page.
const bodies = new Map();
const bodyFor = (slug) => {
  if (!bodies.has(slug)) {
    bodies.set(
      slug,
      lazy(() =>
        import(/* webpackChunkName: "post-[request]" */ `@/data/blog/generated/posts/${slug}.json`).then((mod) => {
          const post = mod.default || mod;
          return { default: () => <BlogBody post={post} /> };
        })
      )
    );
  }
  return bodies.get(slug);
};

export default function BlogPost() {
  const { slug } = useParams();
  const post = POSTS.find((p) => p.slug === slug);
  if (!post) return <NotFound />;

  const Body = bodyFor(slug);
  const path = `/blog/${slug}`;
  const related = POSTS.filter((p) => p.slug !== slug)
    .map((p, i) => ({ p, score: (p.category === post.category ? 0 : 1) * 1000 + i }))
    .sort((a, b) => a.score - b.score)
    .slice(0, 3)
    .map(({ p }) => p);
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "@id": `${SITE}${path}#article`,
      headline: post.h1,
      description: post.description,
      inLanguage: "fr-FR",
      datePublished: post.date,
      dateModified: post.updated,
      articleSection: post.category,
      keywords: post.tags.join(", "),
      image: `${SITE}/og-image.png`,
      mainEntityOfPage: { "@id": `${SITE}${path}#webpage` },
      isPartOf: { "@id": `${SITE}/blog#blog` },
      author: { "@id": `${SITE}/#organization` },
      publisher: { "@id": `${SITE}/#organization` },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Accueil", item: `${SITE}/` },
        { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE}/blog` },
        { "@type": "ListItem", position: 3, name: post.h1, item: `${SITE}${path}` },
      ],
    },
  ];

  return (
    <article className="mx-auto max-w-[1200px] px-5 sm:px-8 py-12 lg:py-20" data-testid="blog-post">
      <Seo title={post.title} description={post.description} path={path} jsonLd={jsonLd} type="article" />
      <nav aria-label="Fil d’Ariane" className="text-sm mb-8 flex flex-wrap gap-2">
        <Link to="/" className="underline">Accueil</Link><span aria-hidden="true">/</span><Link to="/blog" className="underline">Blog</Link>
      </nav>
      <p className="font-mono text-xs uppercase tracking-widest text-ink/70 mb-4">
        <span className="text-accent">{post.category}</span> · publié le <time dateTime={post.date}>{formatDateFr(post.date)}</time>
        {post.updated !== post.date && <> · mis à jour le {formatDateFr(post.updated)}</>} · {post.readingTime} min de lecture
      </p>
      <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight leading-tight max-w-4xl">{hy(post.h1)}</h1>
      <p className="mt-6 text-lg leading-relaxed max-w-3xl text-ink/80">{post.description}</p>

      <Suspense fallback={<div className="min-h-[60vh]" aria-busy="true" />}>
        <Body />
      </Suspense>

      <section className="my-12 max-w-3xl border-2 border-ink bg-white p-6 sm:p-8">
        <h2 className="font-display text-2xl font-bold mb-3">Faire le point sur votre assurance</h2>
        <p className="text-ink/75 leading-relaxed mb-6">
          Décrivez votre activité : RC Pro et décennale sont étudiées en une seule demande, gratuite et sans engagement.
        </p>
        <Link
          to="/devis"
          onClick={() => trackEvent("blog_quote_click", { article: slug })}
          className="inline-flex items-center gap-3 border-2 border-ink bg-accent px-6 py-4 font-bold text-paper hover:bg-ink"
        >
          Demander mon devis <ArrowRight aria-hidden="true" className="h-4 w-4" />
        </Link>
      </section>

      {related.length > 0 && (
        <section className="my-12" aria-labelledby="related-title">
          <h2 id="related-title" className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-tight mb-6">À lire aussi</h2>
          <ul className="grid gap-4 md:grid-cols-3">
            {related.map((p) => (
              <li key={p.slug}>
                <Link to={`/blog/${p.slug}`} className="group flex h-full flex-col border border-ink/25 bg-white p-6 hover:border-accent">
                  <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink/65">{p.category} · {formatDateFr(p.date)}</span>
                  <span className="mt-3 font-display text-lg font-bold group-hover:text-accent">{p.h1}</span>
                  <span className="mt-auto pt-4 flex items-center gap-2 text-sm font-bold">Lire <ArrowUpRight aria-hidden="true" className="h-4 w-4" /></span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
      <p className="border-t border-ink/20 pt-6">
        <Link to="/blog" className="underline underline-offset-4 hover:text-accent">Tous les articles du blog</Link>
      </p>
    </article>
  );
}
